/** @jsx jsx */
import type { Interpolation, Theme } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import type { ReactNode } from 'react';
import * as React from 'react';

import { Grid, Typography, Typography2, breakpoints } from '@coursera/cds-core';

type Props = {
  backgroundImage?: string | null;
  title?: string | null;
  subtitle?: string | null;
  description?: ReactNode | string | null;
  cta?: ReactNode;
};

const getStyles: (props?: Props) => Record<string, Interpolation<Theme>> = ({ backgroundImage } = {}) => ({
  wrapper: () => css`
    background: url(${backgroundImage});
    min-height: 400px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: right;
    max-width: 1600px;

    ${breakpoints.down('sm')} {
      background: none;
      margin: var(--cds-spacing-300) 0;
    }
  `,
  innerContainer: () => css`
    max-width: 60%;
    padding: 0 var(--cds-spacing-600);

    @media (max-width: 1200px) {
      max-width: 50%;
    }

    ${breakpoints.down('sm')} {
      max-width: 100%;
    }
  `,
  title: () => css`
    padding-bottom: var(--cds-spacing-200);
  `,
  subtitle: () => css`
    padding-bottom: var(--cds-spacing-300);
  `,
  description: () => css`
    padding-bottom: var(--cds-spacing-300);
  `,
  linkButton: () => css`
    display: inline-block;
    background-color: var(--cds-color-blue-700);
    box-shadow: inset 0 0 0 1px var(--cds-color-blue-700);
    padding: 12px 32px;
    border-radius: 4px;
    line-height: 24px;
    font-weight: 700;
  `,
});

const ConsumerHeroModule: React.FC<Props> = ({ title, subtitle, backgroundImage, description, cta }) => {
  const styles = getStyles({ backgroundImage });

  return (
    <Grid container justifyContent="center">
      <Grid container alignContent="center" css={styles.wrapper}>
        <Grid container direction="column" css={styles.innerContainer}>
          <Grid css={styles.title}>
            <Typography variant="d2semibold" component="h1">
              {title}
            </Typography>
          </Grid>
          <Grid item css={styles.subtitle}>
            <Typography variant="h2">{subtitle}</Typography>
          </Grid>
          <Grid item css={styles.description}>
            <Typography2 component="h3" variant="subtitleMedium">
              {description}
            </Typography2>
          </Grid>
          <Grid item>{cta}</Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ConsumerHeroModule;
